// src/utils/dataManager.ts
// Enhanced data manager with validation methods matching Python's data.py

export interface TransportMode {
  value: string;
  label: string;
  longitudinal_forward_acc_coef: number;
  longitudinal_backward_acc_coef: number;
  transverse_right_acc_coef: number;
  transverse_left_acc_coef: number;
}

export interface TransportUnit {
  value: string;
  label: string;
  max_weight: number;
  con_strength: number;
  con_strength_loop: number;
  compatible_modes: string[];
  length: number;
  width: number;
  height: number;
}

export interface Material {
  value: string;
  label: string;
  friction_coefficient: number;
}

export interface HalatType {
  type: string;
  name: string;
  toka: string;
  cordstrap_name?: string;
  sbs: number;
  d_lashing_msl: number;
  l_lashing_msl: number;
  msl_tur: string;
  compatible_units: string[];
}

export class EnhancedDataManager {
  private transportModes: TransportMode[] = [];
  private transportUnits: TransportUnit[] = [];
  private materials: Material[] = [];
  private halatTypes: HalatType[] = [];
  private version: string = '2.0';
  private loaded: boolean = false;
  // Use relative URL to avoid CORS issues - the API is served from the same origin
  // Error tracking removed as it wasn't being used

  /**
   * Fetch data from backend API
   */

  private async fetchFromBackend<T>(endpoint: string): Promise<T> {
    // Use new API client with timeout and retry
    const { apiClient } = await import('./api-client');
    const { dataLogger } = await import('./logger');

    // Remove leading slash from endpoint if present
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
    dataLogger.debug(`Fetching from endpoint: ${cleanEndpoint}`);

    try {
      const data = await apiClient.get<T>(`/api/${cleanEndpoint}`, {
        timeout: 30000, // 30 seconds
        retries: 2,
      });
      dataLogger.info('Successfully fetched data', { endpoint: cleanEndpoint });
      return data;
    } catch (error: any) {
      dataLogger.error('Fetch failed', error);
      throw error;
    }
  }

  /**
   * Load all data from backend (call once on app init)
   */
  /**
   * Log detailed information about transport data for debugging
   */
  private logTransportData(): void {
    // Dynamically import logger to avoid circular dependencies if any
    import('./logger').then(({ dataLogger }) => {
      if (!this.loaded) {
        dataLogger.warn('Cannot log transport data: Data not loaded');
        return;
      }

      dataLogger.group('[DataManager] Transport Data Debug');

      // Log available transport modes
      dataLogger.debug('Available Transport Modes:');
      this.transportModes.forEach(mode => {
        dataLogger.debug(`- ${mode.label} (${mode.value})`);
      });

      // Log transport units and their compatible modes
      dataLogger.debug('\nTransport Units and Compatible Modes:');
      this.transportUnits.forEach(unit => {
        const modeDetails = this.transportModes
          .filter(m => unit.compatible_modes?.includes(m.value))
          .map(m => `${m.label} (${m.value})`)
          .join(', ');

        dataLogger.debug(`\n${unit.label} (${unit.value}):`);
        dataLogger.debug(`  - Max Weight: ${unit.max_weight} MT`);
        dataLogger.debug(`  - Connection Strength (Direct): ${unit.con_strength}`);
        dataLogger.debug(`  - Connection Strength (Loop): ${unit.con_strength_loop}`);
        dataLogger.debug(`  - Compatible Modes: ${modeDetails || 'None'}`);
        dataLogger.debug(`  - Compatible Modes Raw:`, unit.compatible_modes);
      });

      dataLogger.groupEnd();
    });
  }

  /**
   * Validate transport units for data integrity
   */
  private validateTransportUnits(): void {
    import('./logger').then(({ dataLogger }) => {
      if (!this.loaded) {
        dataLogger.warn('Cannot validate transport units: Data not loaded');
        return;
      }

      const invalidUnits = this.transportUnits.filter(u =>
        !u.compatible_modes ||
        !Array.isArray(u.compatible_modes) ||
        u.compatible_modes.length === 0
      );

      if (invalidUnits.length > 0) {
        dataLogger.warn('Found transport units with invalid or empty compatible_modes:',
          invalidUnits.map(u => `${u.label} (${u.value})`));
      }

      // Check for duplicate units
      const unitValues = this.transportUnits.map(u => u.value);
      const uniqueValues = new Set(unitValues);
      if (unitValues.length !== uniqueValues.size) {
        const duplicates = unitValues.filter((item, index) =>
          unitValues.indexOf(item) !== index
        );
        dataLogger.error('Found duplicate transport unit values:', [...new Set(duplicates)]);
      }
    });
  }

  /**
   * Test compatibility between a specific transport unit and mode
   * @param unitValue The transport unit value to test
   * @param modeValue The transport mode value to test against
   */
  testTransportUnitCompatibility(unitValue: string, modeValue: string): void {
    import('./logger').then(({ dataLogger }) => {
      dataLogger.group(`[Test] Checking compatibility for ${unitValue} with ${modeValue}`);

      const unit = this.getTransportUnit(unitValue);
      if (!unit) {
        dataLogger.error(`❌ Unit "${unitValue}" not found!`);
        dataLogger.groupEnd();
        return;
      }

      const mode = this.transportModes.find(m => m.value === modeValue);
      dataLogger.debug(`🔍 Unit: ${unit.label} (${unit.value})`);
      dataLogger.debug(`🚚 Mode: ${mode?.label || 'Unknown'} (${modeValue})`);
      dataLogger.debug(`🔗 Compatible Modes:`, unit.compatible_modes || 'None');

      const isCompatible = unit.compatible_modes?.some(mode =>
        mode.toLowerCase() === modeValue.toLowerCase()
      ) || false;

      dataLogger.debug(`✅ Compatibility: ${isCompatible ? '✅' : '❌'}`);

      if (!isCompatible) {
        dataLogger.warn(`❌ Unit "${unitValue}" is not compatible with mode "${modeValue}"`);
        dataLogger.debug('📋 Available modes for this unit:', unit.compatible_modes || 'None');
      }

      dataLogger.groupEnd();
    });
  }

  async load(): Promise<void> {
    const { dataLogger } = await import('./logger');

    if (this.loaded) {
      dataLogger.debug('Data already loaded, skipping');
      return;
    }

    try {
      dataLogger.info('Starting to load data from backend');
      dataLogger.debug('Current state', {
        loaded: this.loaded,
        transportModes: this.transportModes.length,
        transportUnits: this.transportUnits.length,
        materials: this.materials.length,
        halatTypes: this.halatTypes.length
      });

      // Reset state
      this.loaded = false;
      this.transportModes = [];
      this.transportUnits = [];
      this.materials = [];
      this.halatTypes = [];

      dataLogger.debug('State reset, fetching data');

      // Fetch all UI data from backend
      const data = await this.fetchFromBackend<{
        transport_modes: Array<{
          value: string;
          label: string;
          longitudinal_forward_acc_coef: number;
          longitudinal_backward_acc_coef: number;
          transverse_right_acc_coef: number;
          transverse_left_acc_coef: number;
        }>;
        transport_units: Array<{
          value: string;
          label: string;
          max_weight: number;
          con_strength: number;
          con_strength_loop: number;
          compatible_modes: string[];
          length: number;
          width: number;
          height: number;
        }>;
        materials: Material[];
        halat_types: HalatType[];
      }>('data'); // Changed from '/api/data' to 'data' since the prefix is already added in fetchFromBackend

      dataLogger.info('Received data from backend', {
        transportModes: data?.transport_modes?.length,
        transportUnits: data?.transport_units?.length,
        materials: data?.materials?.length,
        halatTypes: data?.halat_types?.length
      });

      // Validate the data structure
      if (!data ||
        !Array.isArray(data.transport_modes) ||
        !Array.isArray(data.transport_units) ||
        !Array.isArray(data.materials) ||
        !Array.isArray(data.halat_types)) {
        const error = new Error('Invalid data format received from server');
        dataLogger.error('Data validation failed', {
          hasTransportModes: Array.isArray(data?.transport_modes),
          hasTransportUnits: Array.isArray(data?.transport_units),
          hasMaterials: Array.isArray(data?.materials),
          hasHalatTypes: Array.isArray(data?.halat_types)
        });
        throw error;
      }

      // Validate transport modes have all required fields
      const invalidModes = data.transport_modes.some(mode => {
        const isInvalid = !mode.value ||
          !mode.label ||
          mode.longitudinal_forward_acc_coef === undefined ||
          mode.longitudinal_backward_acc_coef === undefined ||
          mode.transverse_right_acc_coef === undefined ||
          mode.transverse_left_acc_coef === undefined;

        if (isInvalid) {
          dataLogger.error('Invalid transport mode', { mode });
        }
        return isInvalid;
      });

      if (invalidModes) {
        throw new Error('One or more transport modes are missing required fields');
      }

      // Set the data - no fallback, just use what's from the API
      this.transportModes = data.transport_modes;
      this.transportUnits = data.transport_units;
      this.materials = data.materials;
      this.halatTypes = data.halat_types;

      this.loaded = true;

      // Log and validate the loaded data
      this.logTransportData();
      this.validateTransportUnits();

      dataLogger.info('Data loaded successfully');
    } catch (error: unknown) {
      dataLogger.error('Failed to load data', error as Error);
      this.loaded = false;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error during data loading';
      throw new Error(`Failed to load data: ${errorMessage}`);
    }
  }

  // ===== GETTERS (matching Python's get_all_* functions) =====

  // Check if data is loaded
  isLoaded(): boolean {
    return this.loaded &&
      Array.isArray(this.transportModes) &&
      Array.isArray(this.transportUnits) &&
      Array.isArray(this.materials) &&
      Array.isArray(this.halatTypes);
  }

  // Ensure data is loaded before access
  private ensureLoaded(): void {
    if (!this.isLoaded()) {
      throw new Error('Data not loaded. Call load() first and await its completion.');
    }
  }

  getTransportModes(): TransportMode[] {
    this.ensureLoaded();
    return [...this.transportModes]; // Return a copy to prevent direct mutations
  }

  getTransportUnits(): TransportUnit[] {
    this.ensureLoaded();
    return [...this.transportUnits]; // Return a copy to prevent direct mutations
  }

  getMaterials(): Material[] {
    this.ensureLoaded();
    return [...this.materials]; // Return a copy to prevent direct mutations
  }

  getHalatTypes(): HalatType[] {
    this.ensureLoaded();
    return [...this.halatTypes]; // Return a copy to prevent direct mutations
  }

  getVersion(): string {
    return this.version;
  }

  // ===== VALIDATION METHODS (matching Python's data.py) =====

  /**
   * Get transport unit by key
   * Python: get_transport_unit(transport_unit: str)
   */
  getTransportUnit(unitKey: string): TransportUnit | null {
    return this.transportUnits.find(u => u.value === unitKey) || null;
  }

  /**
   * Get transport mode by key
   */
  getTransportMode(modeKey: string): TransportMode | null {
    return this.transportModes.find(m => m.value === modeKey) || null;
  }

  /**
   * Get material by key
   */
  getMaterial(materialKey: string): Material | null {
    return this.materials.find(m => m.value === materialKey) || null;
  }

  /**
   * Validate transport combination
   * Python: validate_transport_combination(mode: str, unit: str)
   */
  validateTransportCombination(
    mode: string,
    unit: string
  ): { valid: boolean; message?: string } {
    if (!mode) {
      return {
        valid: false,
        message: 'No transport mode provided'
      };
    }

    if (!unit) {
      return {
        valid: false,
        message: 'No transport unit provided'
      };
    }

    const transportUnit = this.getTransportUnit(unit);

    if (!transportUnit) {
      const availableUnits = this.transportUnits.map(u => u.value).join(', ');
      return {
        valid: false,
        message: `Invalid transport unit: "${unit}". Available units: ${availableUnits || 'None'}`
      };
    }

    const normalizedMode = mode.toLowerCase();
    const isCompatible = transportUnit.compatible_modes?.some(
      m => m?.toLowerCase() === normalizedMode
    ) || false;

    if (!isCompatible) {
      const modeObj = this.getTransportMode(mode);
      const availableModes = transportUnit.compatible_modes
        ?.map(m => this.getTransportMode(m)?.label || m)
        .join(', ') || 'none';

      return {
        valid: false,
        message: `"${transportUnit.label}" is not compatible with "${modeObj?.label || mode}". ` +
          `Compatible modes: ${availableModes}`
      };
    }

    return {
      valid: true,
      message: `"${transportUnit.label}" is compatible with "${mode}"`
    };
  }

  /**
   * Validate transport unit exists
   * Python: validate_transport_unit(transport_unit: str)
   */
  validateTransportUnit(unitKey: string): boolean {
    return this.transportUnits.some(u => u.value === unitKey);
  }

  /**
   * Get acceleration coefficient for a mode and direction
   * Python: get_acceleration_coefficient(mode, coef_key)
   */
  getAccelerationCoefficient(
    mode: string,
    direction: 'longitudinal_forward_acc_coef' |
      'longitudinal_backward_acc_coef' |
      'transverse_right_acc_coef' |
      'transverse_left_acc_coef'
  ): number | null {
    const modeObj = this.getTransportMode(mode);
    return modeObj ? modeObj[direction] : null;
  }

  /**
   * Get friction coefficient for a material
   */
  getFrictionCoefficient(materialKey: string): number | null {
    const material = this.getMaterial(materialKey);
    return material ? material.friction_coefficient : null;
  }

  /**
   * Get valid transport units for a mode
   * Python: get_valid_transport_units(mode: str)
   */
  getValidTransportUnits(mode: string): TransportUnit[] {
    if (!mode) {
      return [];
    }

    const normalizedMode = mode.toLowerCase().trim();

    // Special handling for combined modes (e.g., 'road_sea_a' should match with 'road_sea_a')
    const filtered = this.transportUnits.filter(u => {
      if (!u.compatible_modes || !Array.isArray(u.compatible_modes)) {
        return false;
      }

      // Check if the mode is in the unit's compatible modes
      const isCompatible = u.compatible_modes.some(compatibleMode => {
        if (!compatibleMode) return false;

        const normalizedCompatibleMode = compatibleMode.toLowerCase().trim();

        // Check for exact match
        if (normalizedCompatibleMode === normalizedMode) {
          return true;
        }

        // For combined modes (e.g., 'road_sea_a'), check if the base mode is included
        // For example, if mode is 'road', it should match 'road_sea_a'
        if (normalizedCompatibleMode.includes('_') &&
          (normalizedCompatibleMode.startsWith(`${normalizedMode}_`) ||
            normalizedCompatibleMode.endsWith(`_${normalizedMode}`) ||
            normalizedCompatibleMode.includes(`_${normalizedMode}_`))) {
          return true;
        }

        return false;
      });

      return isCompatible;
    });

    return filtered;
  }

  /**
   * Find compatible loop lashing halat types for a transport unit
   * Python: find_loop_lashing_halatlar(transport_unit: str)
   */
  findLoopLashingHalatlar(transportUnit: string): HalatType[] {
    return this.halatTypes.filter(
      h => h.l_lashing_msl > 0 && h.compatible_units.includes(transportUnit)
    );
  }

  /**
   * Find compatible direct lashing halat types for a transport unit (with capped MSL values)
   * Python: find_capped_lashing_cables(transport_unit: str)
   */
  async findDirectLashingHalatlar(transportUnit: string, isOversizedWidth: boolean = false): Promise<HalatType[]> {
    const { dataLogger } = await import('./logger');

    if (!transportUnit) {
      dataLogger.warn('[findDirectLashingHalatlar] No transport unit provided');
      return [];
    }

    try {
      // Fetch capped halat data from backend API
      const query = isOversizedWidth ? `?is_oversized_width=true` : '';
      const cappedHalats = await this.fetchFromBackend<Array<{
        'Halat Türü': string;
        'TOKA Türü': string;
        'cordstrap_name'?: string;
        'D_Lashing_MSL': number;
        'L_Lashing_MSL': number;
        'MSL Türü': string;
      }>>(`data/capped-halats/${transportUnit}${query}`);

      // Convert backend format to frontend HalatType format
      return cappedHalats.map(halat => ({
        type: halat['Halat Türü'],
        name: halat['Halat Türü'], // Use same value for name
        toka: halat['TOKA Türü'],
        cordstrap_name: halat['cordstrap_name'] || `${halat['Halat Türü']} ${halat['TOKA Türü']}`,
        sbs: 0, // Not provided in capped data
        d_lashing_msl: halat['D_Lashing_MSL'],
        l_lashing_msl: halat['L_Lashing_MSL'],
        msl_tur: halat['MSL Türü'] || '', // Map MSL Türü from backend
        compatible_units: [transportUnit] // Only this transport unit
      }));

    } catch (error) {
      dataLogger.error(`[findDirectLashingHalatlar] Failed to fetch capped halats for ${transportUnit}:`, error);
      // Fallback to local data if API fails
      return this.halatTypes.filter(
        h => h.d_lashing_msl > 0 && h.compatible_units.includes(transportUnit)
      );
    }
  }

  /**
   * Validate weight against transport unit limits
   */
  validateWeight(weight: number, transportUnit: string): {
    valid: boolean;
    maxWeight?: number;
    message?: string;
  } {
    const unit = this.getTransportUnit(transportUnit);

    if (!unit) {
      return {
        valid: false,
        message: 'Invalid transport unit'
      };
    }

    if (weight > unit.max_weight) {
      return {
        valid: false,
        maxWeight: unit.max_weight,
        message: `Weight ${weight} MT exceeds ${unit.label} maximum of ${unit.max_weight} MT`
      };
    }

    return { valid: true, maxWeight: unit.max_weight };
  }

  /**
   * Get all data needed for calculation (convenience method)
   */
  getCalculationData(
    modeKey: string,
    unitKey: string,
    materialKey: string
  ): {
    mode: TransportMode | null;
    unit: TransportUnit | null;
    material: Material | null;
    compatible: boolean;
  } {
    const mode = this.getTransportMode(modeKey);
    const unit = this.getTransportUnit(unitKey);
    const material = this.getMaterial(materialKey);

    const compatible = mode && unit
      ? this.validateTransportCombination(modeKey, unitKey).valid
      : false;

    return { mode, unit, material, compatible };
  }
}

// Export singleton instance (like Python's module-level access)
export const enhancedDataManager = new EnhancedDataManager();

// Export type guards
export const isValidTransportMode = (value: string): value is string => {
  return enhancedDataManager.getTransportMode(value) !== null;
};

export const isValidTransportUnit = (value: string): value is string => {
  return enhancedDataManager.validateTransportUnit(value);
};

export const isValidMaterial = (value: string): value is string => {
  return enhancedDataManager.getMaterial(value) !== null;
};